import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * DTO fuer das plattform-weite System-Branding (BauAgent.ai).
 */
export interface SystemBrandingDto {
  name: string;
  primaerFarbe: string;
  primaerFarbeDunkel: string;
  akzentFarbe: string;
  faviconFarbe: string;
  logoSvg?: string;
  logoSvgDark?: string;
  logoBildUrl?: string;
  webseite?: string;
}

/**
 * Laedt und cached das System-Branding (BauAgent.ai).
 * Wird fuer Auth-Seiten (Login, Registrierung, Bestaetigung) und
 * System-E-Mails verwendet. Unabhaengig vom Tenant-Branding.
 *
 * AllowAnonymous-Endpoint – kein Login noetig.
 */
@Injectable({ providedIn: 'root' })
export class SystemBrandingService {

  private readonly _branding$ = new BehaviorSubject<SystemBrandingDto>(this.defaults());

  /** Observable auf das aktuelle System-Branding. */
  readonly branding$ = this._branding$.asObservable();

  /** Aktuelles Branding (synchron). */
  get branding(): SystemBrandingDto {
    return this._branding$.value;
  }

  constructor(private http: HttpClient) {}

  /**
   * Laedt das System-Branding vom Backend.
   * Wird beim App-Start aufgerufen (APP_INITIALIZER oder manuell).
   */
  laden(ignoreHostname = false): Observable<SystemBrandingDto> {
    const hostname = ignoreHostname ? '' : window.location.hostname;
    const params = hostname ? `?hostname=${encodeURIComponent(hostname)}` : '';
    return this.http.get<SystemBrandingDto>(
      `${environment.apiUrl}/api/system/branding${params}`
    ).pipe(
      tap(branding => this._branding$.next(branding)),
      catchError(() => {
        // Fallback bei Fehler: Defaults verwenden
        return of(this.defaults());
      })
    );
  }

  /**
   * Aktualisiert das System-Branding (nur SystemAdmin).
   */
  aktualisieren(dto: SystemBrandingDto): Observable<SystemBrandingDto> {
    return this.http.put<SystemBrandingDto>(
      `${environment.apiUrl}/api/system/branding`, dto
    ).pipe(tap(branding => this._branding$.next(branding)));
  }

  /** Default-Werte (BauAgent.ai Orange – identisch mit Backend). */
  private defaults(): SystemBrandingDto {
    return {
      name: 'BauAgent.ai',
      primaerFarbe: '#e8741e',
      primaerFarbeDunkel: '#c85f15',
      akzentFarbe: '#ffffff',
      faviconFarbe: '#e8741e',
      logoBildUrl: 'img/logo.png',
      webseite: 'https://bauagent.ai',
    };
  }
}
